import { Box, svgIconClasses, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import {
  FavoritePresetsSection,
  VIEW_VALIDATORS,
} from '@/features/savedPresets';
import {
  selectIsOrganizationDataAccessible,
  selectViewsRestrictions,
  ViewAccessType,
} from '@/entities/session';
import { ROUTES } from '@/shared/constants';
import { Analytics } from '@/shared/lib';
import { useAppSelector } from '@/shared/model';
import {
  CalendarChartIcon,
  CalendarInsightsIcon,
  CohortChartIcon,
  CohortMarketingPerformanceIcon,
  CohortRetentionIcon,
  CohortScannerIcon,
  ROIBreakdownIcon,
  PresetsIcon,
} from '@/shared/ui';

const PanelRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'visible',
})<{ visible?: boolean }>(({ visible }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  bottom: 0,
  width: 280,
  zIndex: 1300,
  backgroundColor: '#f1f3f8',
  borderLeft: '1px solid #dddfe7',
  borderTopRightRadius: 16,
  borderBottomRightRadius: 16,
  boxShadow: '0px 2px 6px 0px rgba(50, 50, 71, 0.16)',
  overflowY: 'auto',
  overflowX: 'hidden',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
  display: 'flex',
  flexDirection: 'column',
  transform: visible ? 'translateX(0)' : 'translateX(-68px)',
  opacity: visible ? 1 : 0,
  pointerEvents: visible ? 'auto' : 'none',
  transition: 'transform 0.2s ease-out, opacity 0.15s ease-out',
}));

const Section = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: `${theme.spacing(2)} ${theme.spacing(1.5)}`,
  borderBottom: '1px solid #dddfe7',
}));

const SectionHeader = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 500,
  lineHeight: 'normal',
  color: '#7e828f',
  paddingLeft: theme.spacing(1),
  textTransform: 'uppercase',
}));

const NavItemLink = styled(NavLink)(({ theme }) => ({
  height: 40,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: `0 ${theme.spacing(1)}`,
  borderRadius: theme.shape.borderRadius * 2,
  textDecoration: 'none',
  color: '#343844',
  flexShrink: 0,

  '&.active': {
    backgroundColor: '#e7ecfa',

    [`& .${svgIconClasses.root}`]: {
      color: theme.palette.primary.main,
    },
  },

  '&:hover:not(.active)': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
}));

const NavItemIcon = styled(Box)(() => ({
  width: 24,
  height: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  color: '#7E828F',

  '& svg': {
    width: 24,
    height: 24,
  },
}));

const NavItemLabel = styled('span')(() => ({
  fontSize: 14,
  fontWeight: 500,
  lineHeight: '18px',
  color: 'inherit',
  flex: '1 0 0',
  minWidth: 0,
}));

function NavItem({
  to,
  icon,
  label,
  tag,
}: {
  to: string;
  icon: ReactNode;
  label: string;
  tag?: ReactNode;
}) {
  const handleClick = () => {
    Analytics.sendFeatureUsage('view_navigation', 'navigation_drawer_item', {
      view: to,
    });
  };

  return (
    <NavItemLink to={to} onClick={handleClick}>
      <NavItemIcon>{icon}</NavItemIcon>
      <NavItemLabel>{label}</NavItemLabel>
      {tag}
    </NavItemLink>
  );
}

function SectionGroup({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <SectionHeader>{title}</SectionHeader>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>{children}</Box>
    </Box>
  );
}

export function ExplorePanel({ visible }: { visible: boolean }) {
  const viewsRestrictions = useAppSelector(selectViewsRestrictions);
  const isOrganizationDataAccessible = useAppSelector(
    selectIsOrganizationDataAccessible,
  );

  const isAnyDataViewAllowed =
    viewsRestrictions?.cohortMarketingPerformance !== ViewAccessType.DENIED ||
    viewsRestrictions?.calendarInsights !== ViewAccessType.DENIED ||
    viewsRestrictions?.cohortChart !== ViewAccessType.DENIED ||
    viewsRestrictions?.calendarChart !== ViewAccessType.DENIED ||
    viewsRestrictions?.cohortScanner !== ViewAccessType.DENIED ||
    viewsRestrictions?.cohortRetention !== ViewAccessType.DENIED ||
    viewsRestrictions?.roiBreakdown !== ViewAccessType.DENIED;

  const hasCohortItems =
    isOrganizationDataAccessible &&
    (viewsRestrictions?.cohortMarketingPerformance !== ViewAccessType.DENIED ||
      viewsRestrictions?.cohortChart !== ViewAccessType.DENIED ||
      viewsRestrictions?.cohortScanner !== ViewAccessType.DENIED ||
      viewsRestrictions?.cohortRetention !== ViewAccessType.DENIED);

  const hasCalendarItems =
    isOrganizationDataAccessible &&
    (viewsRestrictions?.calendarInsights !== ViewAccessType.DENIED ||
      viewsRestrictions?.calendarChart !== ViewAccessType.DENIED);

  return (
    <PanelRoot visible={visible}>
      <Section sx={{ pt: 3 }}>
        {hasCohortItems && (
          <SectionGroup title="Cohort">
            {isOrganizationDataAccessible &&
              viewsRestrictions?.cohortMarketingPerformance !==
                ViewAccessType.DENIED && (
                <NavItem
                  to={ROUTES.cohortMarketingPerformance}
                  icon={<CohortMarketingPerformanceIcon />}
                  label="Cohort Marketing Performance"
                />
              )}
            {isOrganizationDataAccessible &&
              viewsRestrictions?.cohortChart !== ViewAccessType.DENIED && (
                <NavItem
                  to={ROUTES.cohortChart}
                  icon={<CohortChartIcon />}
                  label="Cohort Chart"
                />
              )}
            {isOrganizationDataAccessible &&
              viewsRestrictions?.cohortRetention !== ViewAccessType.DENIED && (
                <NavItem
                  to={ROUTES.cohortRetention}
                  icon={<CohortRetentionIcon />}
                  label="Cohort Retention"
                />
              )}
            {isOrganizationDataAccessible &&
              viewsRestrictions?.cohortScanner !== ViewAccessType.DENIED && (
                <NavItem
                  to={ROUTES.cohortScanner}
                  icon={<CohortScannerIcon />}
                  label="Cohort Scanner"
                />
              )}
          </SectionGroup>
        )}
        {hasCalendarItems && (
          <SectionGroup title="Calendar">
            {isOrganizationDataAccessible &&
              viewsRestrictions?.calendarInsights !== ViewAccessType.DENIED && (
                <NavItem
                  to={ROUTES.calendarInsights}
                  icon={<CalendarInsightsIcon />}
                  label="Calendar Insights"
                />
              )}
            {isOrganizationDataAccessible &&
              viewsRestrictions?.calendarChart !== ViewAccessType.DENIED && (
                <NavItem
                  to={ROUTES.calendarChart}
                  icon={<CalendarChartIcon />}
                  label="Calendar Chart"
                />
              )}
            {isOrganizationDataAccessible &&
              viewsRestrictions?.roiBreakdown !== ViewAccessType.DENIED && (
                <NavItem
                  to={ROUTES.roiBreakdown}
                  icon={<ROIBreakdownIcon />}
                  label="ROI Breakdown"
                />
              )}
          </SectionGroup>
        )}
      </Section>

      {isAnyDataViewAllowed && (
        <>
          <Section>
            <NavItem
              to={ROUTES.presetsPage}
              icon={<PresetsIcon />}
              label="All Presets"
            />
          </Section>
          <Section
            sx={{
              borderBottom: 'none',
              flex: 1,
              overflow: 'hidden',
              minHeight: 0,
            }}
          >
            <FavoritePresetsSection validators={VIEW_VALIDATORS} />
          </Section>
        </>
      )}
    </PanelRoot>
  );
}
